import React, { useState, useEffect, useRef } from 'react';
import './Login.css';

const STEP = {
  CREDENTIALS:    'credentials',
  MFA:            'mfa',
  NEW_PASSWORD:   'new_password',
  FORGOT_PASS:    'forgot_password',
  FORGOT_CONFIRM: 'forgot_confirm'
};

const RESEND_COOLDOWN = 30;

// Requisitos de senha (espelham a política do Cognito)
const PASSWORD_RULES = [
  { id: 'length',  label: 'Mínimo 8 caracteres',              test: v => v.length >= 8 },
  { id: 'upper',   label: 'Pelo menos uma letra maiúscula',   test: v => /[A-Z]/.test(v) },
  { id: 'lower',   label: 'Pelo menos uma letra minúscula',   test: v => /[a-z]/.test(v) },
  { id: 'number',  label: 'Pelo menos um número',             test: v => /[0-9]/.test(v) },
  { id: 'special', label: 'Pelo menos um caractere especial (@!#$%...)', test: v => /[^A-Za-z0-9]/.test(v) }
];

function Login({ onLogin }) {
  const [step, setStep]                     = useState(STEP.CREDENTIALS);
  const [email, setEmail]                   = useState('');
  const [password, setPassword]             = useState('');
  const [mfaCode, setMfaCode]               = useState('');
  const [newPass, setNewPass]               = useState('');
  const [newPassConfirm, setNewPassConfirm] = useState('');
  const [resetCode, setResetCode]           = useState('');
  const [resetPass, setResetPass]           = useState('');
  const [session, setSession]               = useState(null);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');
  const [success, setSuccess]               = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showPassRules, setShowPassRules]   = useState(false);
  const cooldownRef = useRef(null);

  useEffect(() => () => { if (cooldownRef.current) clearInterval(cooldownRef.current); }, []);

  const startCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;
  const REGION    = process.env.REACT_APP_COGNITO_REGION || 'us-east-1';
  const ENDPOINT  = `https://cognito-idp.${REGION}.amazonaws.com`;

  const cognitoRequest = async (action, body) => {
    const res = await fetch(ENDPOINT, {
      method:  'POST',
      headers: {
        'Content-Type':  'application/x-amz-json-1.1',
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`
      },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw { code: data.__type, message: data.message };
    return data;
  };

  const saveTokens = (result) => {
    localStorage.setItem('idToken',        result.IdToken);
    localStorage.setItem('accessToken',    result.AccessToken);
    localStorage.setItem('refreshToken',   result.RefreshToken);
    localStorage.setItem('tokenExpiresAt', Date.now() + result.ExpiresIn * 1000);

    // Decodificar o idToken para extrair atributos do usuário
    try {
      const payload   = JSON.parse(atob(result.IdToken.split('.')[1]));
      const email     = payload.email     || '';
      const empresaId = payload['custom:empresa_id'] || 'default';
      localStorage.setItem('userEmail',  email);
      localStorage.setItem('empresaId',  empresaId);
      console.log(`Login: email=${email} empresaId=${empresaId}`);
    } catch (e) {
      console.error('Erro ao decodificar token:', e);
    }
  };

  // ── Etapa 1: Credenciais ──────────────────────
  const handleCredentials = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setLoading(true);
    try {
      // Passo 1: USER_PASSWORD_AUTH para detectar NEW_PASSWORD_REQUIRED
      const data = await cognitoRequest('InitiateAuth', {
        AuthFlow:       'USER_PASSWORD_AUTH',
        ClientId:       CLIENT_ID,
        AuthParameters: { USERNAME: email, PASSWORD: password }
      });

      // Senha temporária — forçar troca antes de qualquer outra coisa
      if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        setSession(data.Session);
        setStep(STEP.NEW_PASSWORD);
        return;
      }

      // Senha já definida — iniciar CUSTOM_AUTH para MFA por e-mail
      const customData = await cognitoRequest('InitiateAuth', {
        AuthFlow:       'CUSTOM_AUTH',
        ClientId:       CLIENT_ID,
        AuthParameters: { USERNAME: email, PASSWORD: password }
      });

      if (customData.ChallengeName === 'CUSTOM_CHALLENGE') {
        setSession(customData.Session);
        setStep(STEP.MFA);
        startCooldown();
        return;
      }

      if (customData.AuthenticationResult) {
        saveTokens(customData.AuthenticationResult);
        _finishLogin();
      }
    } catch (err) {
      setError(_translateError(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Etapa 2: Código MFA ───────────────────────
  const handleMFA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await cognitoRequest('RespondToAuthChallenge', {
        ClientId:           CLIENT_ID,
        ChallengeName:      'CUSTOM_CHALLENGE',
        Session:            session,
        ChallengeResponses: { USERNAME: email, ANSWER: mfaCode }
      });

      if (data.AuthenticationResult) {
        saveTokens(data.AuthenticationResult);
        _finishLogin();
        return;
      }

      // Código incorreto — Cognito retorna novo CUSTOM_CHALLENGE
      if (data.ChallengeName === 'CUSTOM_CHALLENGE') {
        setSession(data.Session);
        setError('Código inválido. Tente novamente.');
        setMfaCode('');
      }
    } catch (err) {
      setError('Código inválido ou expirado.');
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  };

  // ── Reenviar código MFA ───────────────────────
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setError(''); setSuccess('');
    setLoading(true);
    try {
      // Responder com código vazio força o Cognito a chamar CreateAuthChallenge novamente
      const data = await cognitoRequest('RespondToAuthChallenge', {
        ClientId:           CLIENT_ID,
        ChallengeName:      'CUSTOM_CHALLENGE',
        Session:            session,
        ChallengeResponses: { USERNAME: email, ANSWER: 'RESEND' }
      });
      if (data.Session) {
        setSession(data.Session);
        setMfaCode('');
        setSuccess('Novo código enviado para o seu e-mail.');
        startCooldown();
      }
    } catch (err) {
      setError('Não foi possível reenviar. Tente fazer login novamente.');
    } finally {
      setLoading(false);
    }
  };

  // ── Etapa 3: Nova senha (primeiro acesso) ─────
  const handleNewPassword = async (e) => {
    e.preventDefault();
    setError('');

    const allValid = PASSWORD_RULES.every(r => r.test(newPass));
    if (!allValid)          { setError('A senha não atende aos requisitos.'); return; }
    if (newPass !== newPassConfirm) { setError('As senhas não coincidem.'); return; }

    setLoading(true);
    try {
      const data = await cognitoRequest('RespondToAuthChallenge', {
        ClientId:           CLIENT_ID,
        ChallengeName:      'NEW_PASSWORD_REQUIRED',
        Session:            session,
        ChallengeResponses: { USERNAME: email, NEW_PASSWORD: newPass }
      });

      if (data.ChallengeName === 'CUSTOM_CHALLENGE') {
        setSession(data.Session);
        setStep(STEP.MFA);
        startCooldown();
        return;
      }
      if (data.AuthenticationResult) {
        saveTokens(data.AuthenticationResult);
        _finishLogin();
      }
    } catch (err) {
      setError(_translateError(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Esqueci senha — solicitar código ──────────
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!email) { setError('Informe o e-mail antes de continuar.'); return; }
    setLoading(true);
    try {
      await cognitoRequest('ForgotPassword', { ClientId: CLIENT_ID, Username: email });
      setSuccess('Código enviado para o seu e-mail.');
      setStep(STEP.FORGOT_CONFIRM);
      startCooldown();
    } catch (err) {
      setError(_translateError(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Esqueci senha — confirmar nova senha ──────
  const handleForgotConfirm = async (e) => {
    e.preventDefault();
    setError('');
    const allValid = PASSWORD_RULES.every(r => r.test(resetPass));
    if (!allValid) { setError('A senha não atende aos requisitos.'); return; }
    setLoading(true);
    try {
      await cognitoRequest('ConfirmForgotPassword', {
        ClientId:         CLIENT_ID,
        Username:         email,
        ConfirmationCode: resetCode,
        Password:         resetPass
      });
      setSuccess('Senha redefinida! Faça o login.');
      setStep(STEP.CREDENTIALS);
      setResetCode(''); setResetPass('');
    } catch (err) {
      setError(_translateError(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Reenviar código de reset ──────────────────
  const handleResendReset = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      await cognitoRequest('ForgotPassword', { ClientId: CLIENT_ID, Username: email });
      setSuccess('Novo código enviado.');
      startCooldown();
    } catch (err) {
      setError('Não foi possível reenviar.');
    } finally {
      setLoading(false);
    }
  };

  const _finishLogin = () => {
    const empresaId = localStorage.getItem('empresaId') || 'default';
    onLogin({ email, empresaId });
  };

  const _translateError = (err) => {
    const map = {
      NotAuthorizedException:        'E-mail ou senha incorretos.',
      UserNotFoundException:          'Usuário não encontrado.',
      UserNotConfirmedException:      'Conta não confirmada. Verifique seu e-mail.',
      PasswordResetRequiredException: 'Redefinição de senha necessária.',
      TooManyRequestsException:       'Muitas tentativas. Aguarde alguns minutos.',
      CodeMismatchException:          'Código incorreto.',
      ExpiredCodeException:           'Código expirado. Solicite um novo.',
      InvalidPasswordException:       'Senha não atende aos requisitos.',
      LimitExceededException:         'Limite de tentativas atingido. Tente mais tarde.'
    };
    return map[err.code] || err.message || 'Erro inesperado. Tente novamente.';
  };

  const goBack = (target = STEP.CREDENTIALS) => {
    setError(''); setSuccess(''); setMfaCode(''); setStep(target);
  };

  // Checagem visual dos requisitos
  const passChecks = PASSWORD_RULES.map(r => ({ ...r, ok: r.test(newPass) }));
  const resetChecks = PASSWORD_RULES.map(r => ({ ...r, ok: r.test(resetPass) }));

  return (
    <div className="login-container">
      <div className="login-box">
        <img src="/logo.png" alt="GestaoFrota" className="logo" onError={e => e.target.style.display = 'none'} />
        <h1>GestaoFrota</h1>
        <p className="login-subtitle">Sistema de Gestão de Multas</p>

        {error   && <p className="login-error">{error}</p>}
        {success && <p className="login-success">{success}</p>}

        {/* ── Etapa 1: Credenciais ── */}
        {step === STEP.CREDENTIALS && (
          <form onSubmit={handleCredentials}>
            <div className="input-group">
              <label>E-mail</label>
              <input type="email" placeholder="seu@email.com" value={email}
                onChange={e => setEmail(e.target.value)} required autoFocus />
            </div>
            <div className="input-group">
              <label>Senha</label>
              <input type="password" placeholder="••••••••" value={password}
                onChange={e => setPassword(e.target.value)} required />
            </div>
            <button type="submit" disabled={loading}>
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
            <button type="button" className="link-btn"
              onClick={() => { setError(''); setSuccess(''); setStep(STEP.FORGOT_PASS); }}>
              Esqueci minha senha
            </button>
          </form>
        )}

        {/* ── Etapa 2: Código MFA ── */}
        {step === STEP.MFA && (
          <form onSubmit={handleMFA}>
            <div className="mfa-info">
              <span className="mfa-icon">📧</span>
              <p>Código enviado para <strong>{email}</strong></p>
            </div>
            <div className="input-group">
              <label>Código de acesso</label>
              <input type="text" placeholder="000000" value={mfaCode} className="mfa-input"
                onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6} required autoFocus />
            </div>
            <button type="submit" disabled={loading || mfaCode.length < 6}>
              {loading ? 'Verificando...' : 'Confirmar'}
            </button>
            <button type="button" className="link-btn" onClick={handleResendCode}
              disabled={resendCooldown > 0 || loading}>
              {resendCooldown > 0 ? `Reenviar código (${resendCooldown}s)` : 'Reenviar código'}
            </button>
            <button type="button" className="back-link" onClick={() => goBack()}>← Voltar</button>
          </form>
        )}

        {/* ── Etapa 3: Nova senha ── */}
        {step === STEP.NEW_PASSWORD && (
          <form onSubmit={handleNewPassword}>
            <div className="new-pass-info">
              <p>Primeiro acesso — crie uma senha pessoal.</p>
            </div>
            <div className="input-group">
              <label>Nova senha</label>
              <input type="password" placeholder="Crie sua senha" value={newPass}
                onChange={e => { setNewPass(e.target.value); setShowPassRules(true); }}
                onFocus={() => setShowPassRules(true)} required autoFocus />
            </div>
            {showPassRules && (
              <div className="pass-rules">
                {passChecks.map(r => (
                  <div key={r.id} className={`pass-rule ${r.ok ? 'ok' : 'fail'}`}>
                    <span>{r.ok ? '✅' : '❌'}</span>
                    <span>{r.label}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="input-group">
              <label>Confirmar senha</label>
              <input type="password" placeholder="Repita a senha" value={newPassConfirm}
                onChange={e => setNewPassConfirm(e.target.value)} required />
            </div>
            <button type="submit" disabled={loading || !PASSWORD_RULES.every(r => r.test(newPass))}>
              {loading ? 'Salvando...' : 'Criar senha e entrar'}
            </button>
          </form>
        )}

        {/* ── Etapa 4: Esqueci senha ── */}
        {step === STEP.FORGOT_PASS && (
          <form onSubmit={handleForgotPassword}>
            <div className="new-pass-info">
              <p>Informe seu e-mail para receber o código de redefinição.</p>
            </div>
            <div className="input-group">
              <label>E-mail</label>
              <input type="email" placeholder="seu@email.com" value={email}
                onChange={e => setEmail(e.target.value)} required autoFocus />
            </div>
            <button type="submit" disabled={loading}>
              {loading ? 'Enviando...' : 'Enviar código'}
            </button>
            <button type="button" className="back-link" onClick={() => goBack()}>← Voltar ao login</button>
          </form>
        )}

        {/* ── Etapa 5: Confirmar reset ── */}
        {step === STEP.FORGOT_CONFIRM && (
          <form onSubmit={handleForgotConfirm}>
            <div className="mfa-info">
              <span className="mfa-icon">📧</span>
              <p>Código enviado para <strong>{email}</strong></p>
            </div>
            <div className="input-group">
              <label>Código recebido</label>
              <input type="text" placeholder="000000" value={resetCode} className="mfa-input"
                onChange={e => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6} required autoFocus />
            </div>
            <div className="input-group">
              <label>Nova senha</label>
              <input type="password" placeholder="Crie sua nova senha" value={resetPass}
                onChange={e => setResetPass(e.target.value)} required />
            </div>
            {resetPass.length > 0 && (
              <div className="pass-rules">
                {resetChecks.map(r => (
                  <div key={r.id} className={`pass-rule ${r.ok ? 'ok' : 'fail'}`}>
                    <span>{r.ok ? '✅' : '❌'}</span>
                    <span>{r.label}</span>
                  </div>
                ))}
              </div>
            )}
            <button type="submit" disabled={loading || resetCode.length < 6 || !PASSWORD_RULES.every(r => r.test(resetPass))}>
              {loading ? 'Redefinindo...' : 'Redefinir senha'}
            </button>
            <button type="button" className="link-btn" onClick={handleResendReset}
              disabled={resendCooldown > 0 || loading}>
              {resendCooldown > 0 ? `Reenviar código (${resendCooldown}s)` : 'Reenviar código'}
            </button>
            <button type="button" className="back-link" onClick={() => goBack()}>← Voltar ao login</button>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;